import apiConfig from 'http/services/api.config'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Image } from 'primereact/image'
import { getCriterionDisplayValue, getCriterionTypeLabel } from '../config/criterion-config'
import { AlarmOutput } from 'domain/alarm-output'
import {
  getAlarmTypeBadgeClass,
  getAlarmTypeIcon,
  getAlarmTypeLabel,
} from '../types/signal-r-alarm-type.helpers'

interface AlarmEventDetailDialogProps {
  container: HTMLDivElement | null
  visible: boolean
  selectedEvent: AlarmOutput | null
  onHide: () => void
}

const AlarmEventDetailDialog: React.FC<AlarmEventDetailDialogProps> = ({
  container,
  visible,
  selectedEvent,
  onHide,
}) => {
  const getImageUrl = (url: string) => {
    if (!url) return ''
    if (url.startsWith('http') || url.startsWith('data:')) {
      return url
    }
    return `${apiConfig.baseUrl}${url.startsWith('/') ? url : `/${url}`}`
  }

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatDateTime = (date: string): string => {
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const typeBodyTemplate = (rowData: AlarmOutput) => {
    return (
      <span
        className={`alarms-type-badge ${getAlarmTypeBadgeClass(rowData.type)}`}
      >
        <i className={`pi ${getAlarmTypeIcon(rowData.type)}`} />
        {getAlarmTypeLabel(rowData.type)}
      </span>
    )
  }

  const eventDetailDialogFooter = (
    <div className='alarms-dialog-footer'>
      <Button
        label='Fechar'
        icon='pi pi-times'
        outlined
        className='alarms-btn-cancel'
        onClick={onHide}
      />
    </div>
  )

  return (
    <Dialog
      appendTo={container}
      visible={visible}
      style={{ width: '800px' }}
      header={
        <div className='alarms-dialog-header'>
          <i className='pi pi-exclamation-triangle alarms-dialog-icon alarms-event-icon' />
          <span>Detalhes do Evento de Alarme</span>
        </div>
      }
      modal
      className='alarms-dialog'
      footer={eventDetailDialogFooter}
      onHide={onHide}
    >
      {selectedEvent && (
        <div className='alarms-event-details'>
          <div className='alarms-event-grid'>
            <div className='alarms-event-image-section'>
              <h4 className='alarms-section-title'>
                <i className='pi pi-image' />
                Evidência Capturada
              </h4>
              <Divider />
              <div className='alarms-event-image-container'>
                {selectedEvent.evidence ? (
                  <Image
                    src={getImageUrl(selectedEvent.evidence)}
                    alt='Imagem do Alarme'
                    width='100%'
                    preview
                    className='alarms-event-image'
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                      const container = target.parentElement
                      if (container) {
                        const placeholder = document.createElement('div')
                        placeholder.className = 'alarms-image-error'
                        placeholder.innerHTML =
                          '<i class="pi pi-exclamation-triangle"></i><p>Erro ao carregar a imagem de evidência</p>'
                        container.appendChild(placeholder)
                      }
                    }}
                  />
                ) : (
                  <div className='alarms-image-error'>
                    <i className='pi pi-image' />
                    <p>Nenhuma evidência disponível</p>
                  </div>
                )}
              </div>
            </div>

            <div className='alarms-event-info-section'>
              <h4 className='alarms-section-title'>
                <i className='pi pi-info-circle' />
                Informações do Alarme
              </h4>
              <Divider />

              <div className='alarms-event-details-grid'>
                <div className='alarms-event-detail-item'>
                  <label className='alarms-detail-label'>
                    <i className='pi pi-tag' />
                    Tipo de Alarme
                  </label>
                  <div className='alarms-detail-value'>
                    {typeBodyTemplate(selectedEvent)}
                  </div>
                </div>

                <div className='alarms-event-detail-item'>
                  <label className='alarms-detail-label'>
                    <i className='pi pi-video' />
                    Câmera
                  </label>
                  <div className='alarms-detail-value'>
                    {selectedEvent.cameraName || 'Câmera desconhecida'}
                  </div>
                </div>

                <div className='alarms-event-detail-item alarms-criteria-section'>
                  <label className='alarms-detail-label'>
                    <i className='pi pi-list' />
                    Critérios Configurados
                  </label>
                  <div className='alarms-detail-value'>
                    {selectedEvent.criterias?.length ? (
                      <div className='alarms-criteria-list'>
                        {selectedEvent.criterias.map((criteria) => (
                          <div
                            key={`${criteria.type}-${criteria.value}`}
                            className='alarms-criteria-list-item'
                          >
                            <span className='alarms-criteria-type'>
                              {getCriterionTypeLabel(criteria.type)}
                            </span>
                            <span className='alarms-criteria-value'>
                              {getCriterionDisplayValue(criteria.value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className='alarm-no-criteria'>Nenhum critério</span>
                    )}
                  </div>
                </div>

                <div className='alarms-event-detail-item'>
                  <label className='alarms-detail-label'>
                    <i className='pi pi-calendar' />
                    Data do Alarme
                  </label>
                  <div className='alarms-detail-value alarms-alarm-time'>
                    <i className='pi pi-exclamation-triangle' />
                    {formatDateTime(selectedEvent.detectedAt)}
                  </div>
                </div>

                <div className='alarms-event-detail-item'>
                  <label className='alarms-detail-label'>
                    <i className='pi pi-user' />
                    Configurado Por
                  </label>
                  <div className='alarms-detail-value'>
                    <i className='pi pi-user-edit mr-1' />
                    {selectedEvent.createdBy}
                  </div>
                </div>

                <div className='alarms-event-detail-item'>
                  <label className='alarms-detail-label'>
                    <i className='pi pi-clock' />
                    Data de Configuração
                  </label>
                  <div className='alarms-detail-value'>
                    {formatDate(selectedEvent.createdDate)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  )
}

export default AlarmEventDetailDialog
